import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { CampaignTemplateInfoProps } from "../../../interfaces/campaignConfig";
import apiClient from "../../../axiosClient";
import useIsMounted from "../../../utils/useMountedRef";

type FieldMapping = {
    column_index: number
    column_name: string
    required: boolean
    is_system_injected?: boolean
}

type TemplateDetails = {
    id: string
    field_mappings: FieldMapping[]
}

const CallCampaignTemplateInfo: React.FC<CampaignTemplateInfoProps> = ({ inputsState }) => {
    const [templateDetails, setTemplateDetails] = useState<TemplateDetails | null>(null)
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)
    const isMountedRef = useIsMounted()

    useEffect(() => {
        const templateName = inputsState['campaign_config_name']
        if (templateName) {
            setIsLoadingTemplate(true)
            apiClient.get(`/api/v1/tcn_config/tcn_template/${templateName}`)
                .then(res => {
                    if (!isMountedRef.current) return
                    setTemplateDetails(res.data)
                })
                .catch(err => {
                    console.error('Failed to fetch template details:', err)
                    if (!isMountedRef.current) return
                    setTemplateDetails(null)
                })
                .finally(() => {
                    if (isMountedRef.current) {
                        setIsLoadingTemplate(false)
                    }
                })
        } else {
            setTemplateDetails(null)
        }
    }, [inputsState['campaign_config_name']])

    const filteredFieldMappings = templateDetails?.field_mappings?.filter(
        (field: FieldMapping) => !field.is_system_injected
    ) || []

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            {isLoadingTemplate && (
                <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading template details...</Typography>
                </Box>
            )}
            {!isLoadingTemplate && templateDetails && filteredFieldMappings.length > 0 && (
                <>
                    <Typography variant="subtitle1" gutterBottom>
                        Required Excel Columns
                    </Typography>
                    <Box 
                        sx={{ 
                            display: 'flex',
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: 0,
                            overflowX: 'auto',
                            overflowY: 'hidden'
                        }}
                    >
                        {filteredFieldMappings.map((field: FieldMapping, index: number) => (
                            <Box
                                key={field.column_index}
                                sx={{
                                    flexShrink: 0,
                                    minWidth: '100px',
                                    bgcolor: 'grey.200',
                                    borderRight: index < filteredFieldMappings.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'grey.300',
                                    textAlign: 'center',
                                    py: 1,
                                    px: 1,
                                    fontSize: '0.8125rem',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {field.column_name}
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default CallCampaignTemplateInfo;
